import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Error Boundary
import ErrorBoundary from './components/ErrorBoundary';

// Theme Context
import { ThemeContextProvider } from './context/ThemeContext';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { RecipeProvider } from './context/RecipeContext';

// Pages
import {
  SplashScreen,
  LoginScreen,
  SignupScreen,
  HomeScreen,
  RecipeListScreen,
  RecipeDetailScreen,
  RecipeStepScreen,
  RecipeCaptureScreen,
  SettingsScreen,
  HelpScreen,
  AboutScreen
} from './pages';

// Routes
import { ROUTES } from './utils/constants';
import { ProtectedRoute } from './components';

// Create a client for React Query
const queryClient = new QueryClient();

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeContextProvider>
          <AuthProvider>
            <RecipeProvider>
              <Router>
                <Routes>
                  {/* Public routes */}
                  <Route path={ROUTES.HOME} element={<SplashScreen />} />
                  <Route path={ROUTES.LOGIN} element={<LoginScreen />} />
                  <Route path={ROUTES.SIGNUP} element={<SignupScreen />} />

                  {/* Protected routes */}
                  <Route
                    path="/home"
                    element={
                      <ProtectedRoute>
                        <HomeScreen />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.RECIPES}
                    element={
                      <ProtectedRoute>
                        <RecipeListScreen />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.RECIPE_DETAIL}
                    element={
                      <ProtectedRoute>
                        <RecipeDetailScreen />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.RECIPE_STEPS}
                    element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <RecipeStepScreen />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.RECIPE_CAPTURE}
                    element={
                      <ProtectedRoute>
                        <RecipeCaptureScreen />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.RECIPE_EDIT}
                    element={
                      <ProtectedRoute>
                        <RecipeCaptureScreen />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SETTINGS}
                    element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <SettingsScreen />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.HELP}
                    element={
                      <ProtectedRoute>
                        <HelpScreen />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.ABOUT}
                    element={
                      <ProtectedRoute>
                        <AboutScreen />
                      </ProtectedRoute>
                    }
                  />

                  {/* Fallback route */}
                  <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
                </Routes>
              </Router>
            </RecipeProvider>
          </AuthProvider>
        </ThemeContextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
